/**
 * Single source of truth for "is the current page a customer-facing route?".
 *
 * Used by AuthContext.performRefresh and customerAuthInterceptor to decide
 * whether a 401-with-no-redirect (the silent-wipe path) should escalate to
 * a soft re-login modal vs. stay silent (admin pages).
 *
 * Treats these as customer routes:
 *   /home, /book, /bookings, /booking/:id, /booking-confirmed/:id,
 *   /booking-submitted/:id, /payment/:id, /payment-result, /profile,
 *   /payment/success
 *
 * Explicitly NOT customer routes (no modal):
 *   /admin/*, /auth, / (landing), /maintenance
 */
export const isCustomerRoute = (pathname = window.location.pathname) => {
  if (!pathname) return false;
  if (pathname.startsWith('/admin')) return false;
  if (pathname.startsWith('/auth')) return false;
  if (pathname === '/' || pathname === '') return false;
  if (pathname.startsWith('/maintenance')) return false;
  // Anything else with a path is a customer route (Home, Book, Bookings,
  // Payment, Profile, etc.).
  return true;
};

/**
 * Dispatch a `customer-session-expired` event. `<SessionExpiredModal />`
 * (mounted in App.js) listens for this and opens an inline login overlay.
 *
 * @param {object|null} pendingConfig Axios config of the request that
 *   triggered the expiry, if any. The modal queues these and replays them
 *   after a successful re-login — letting users continue mid-flow instead
 *   of being bounced to /auth.
 */
export const fireCustomerSessionExpired = (pendingConfig = null) => {
  try {
    window.dispatchEvent(new CustomEvent('customer-session-expired', {
      detail: { config: pendingConfig },
    }));
  } catch (_) {
    // CustomEvent unsupported — fall back to a hard redirect rather than
    // leaving the user silently stranded.
    if (typeof window !== 'undefined' && window.location) {
      window.location.href = '/auth';
    }
  }
};
